// src/pages/ChatSpace/ChatSpaceDistribute.js
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, Grid, Card, CardContent, CardActions,
    TextField, Dialog, DialogTitle, DialogContent, DialogActions,
    IconButton, Alert, Snackbar, InputAdornment, Tooltip,
    FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import {
    ArrowBack, Link as LinkIcon, Code, QrCode,
    ContentCopy, Download, OpenInNew, Share,
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { APPLICATION_URL } from '../../../config';
import { useChatbots } from '../../../contexts/ChatbotContext';

const ChatSpaceDistribute = () => {
    const { chatbotId } = useParams();
    const navigate = useNavigate();
    const { activeChatbot } = useChatbots();

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Dialog states
    const [embedDialog, setEmbedDialog] = useState(false);
    const [qrDialog, setQrDialog] = useState(false);

    // Embed settings
    const [embedSettings, setEmbedSettings] = useState({
        type: 'iframe',
        width: '100%',
        height: '600px',
    });

    const chatbotIdentity = activeChatbot?.chatbot_identity || chatbotId;

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const getPublicLink = () => {
        if (!chatbotIdentity) return 'Loading...';
        return `${APPLICATION_URL}/cs/${chatbotIdentity}`;
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        showSnackbar('Copied to clipboard!');
    };

    const generateEmbedCode = () => {
        const url = getPublicLink();
        switch (embedSettings.type) {
            case 'iframe':
                return `<iframe src="${url}" width="${embedSettings.width}" height="${embedSettings.height}" frameborder="0" allow="microphone"></iframe>`;
            case 'popup':
                return `<script>\nfunction openChatSpace() {\n  window.open('${url}', 'chatspace', 'width=800,height=700,scrollbars=yes');\n}\n</script>\n<button onclick="openChatSpace()">Open Chat Space</button>`;
            case 'slider':
                return `<div id="chatspace-container" style="position:fixed;bottom:20px;right:20px;z-index:9999;">\n  <button onclick="document.getElementById('chatspace-frame').style.display=document.getElementById('chatspace-frame').style.display==='none'?'block':'none'" style="padding:12px 24px;border-radius:8px;border:none;background:#3b82f6;color:white;cursor:pointer;font-weight:600;">Chat Space</button>\n  <iframe id="chatspace-frame" src="${url}" style="display:none;position:fixed;bottom:80px;right:20px;width:400px;height:600px;border:none;border-radius:12px;box-shadow:0 8px 32px rgba(0,0,0,0.15);z-index:9999;" allow="microphone"></iframe>\n</div>`;
            default:
                return '';
        }
    };

    return (
        <Box sx={{ p: 3, mt: 10 }}>
            {/* Header */}
            <Box display="flex" alignItems="center" mb={3}>
                <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/chat-space`)} sx={{ mr: 2 }}>
                    <ArrowBack />
                </IconButton>
                <Box flex={1}>
                    <Typography variant="h5" fontWeight="bold">
                        Distribute Chat Space
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Share your Chat Space with anyone via link, embed code, or QR code
                    </Typography>
                </Box>
            </Box>

            {/* Quick Share Link */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                    Shareable Link
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Anyone with this link can access your Chat Space. Guests will need to enter their name and email before chatting.
                </Typography>
                <TextField
                    fullWidth
                    value={getPublicLink()}
                    InputProps={{
                        readOnly: true,
                        endAdornment: (
                            <InputAdornment position="end">
                                <Tooltip title="Copy Link">
                                    <IconButton onClick={() => copyToClipboard(getPublicLink())}>
                                        <ContentCopy />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Open in New Tab">
                                    <IconButton onClick={() => window.open(getPublicLink(), '_blank')}>
                                        <OpenInNew />
                                    </IconButton>
                                </Tooltip>
                            </InputAdornment>
                        ),
                    }}
                />
            </Paper>

            {/* Distribution Channels */}
            <Typography variant="h6" gutterBottom>Distribution Options</Typography>
            <Grid container spacing={3} mb={3}>
                {/* Embed Code */}
                <Grid item xs={12} md={4}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CardContent sx={{ flex: 1 }}>
                            <Box display="flex" alignItems="center" mb={2}>
                                <Code sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                                <Box>
                                    <Typography variant="h6">Embed Code</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Embed Chat Space on your website
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                Choose between inline iframe, popup window, or slide-in widget. Copy the code and paste it into your website's HTML.
                            </Typography>
                        </CardContent>
                        <CardActions>
                            <Button startIcon={<Code />} onClick={() => setEmbedDialog(true)}>
                                Get Embed Code
                            </Button>
                        </CardActions>
                    </Card>
                </Grid>

                {/* QR Code */}
                <Grid item xs={12} md={4}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CardContent sx={{ flex: 1 }}>
                            <Box display="flex" alignItems="center" mb={2}>
                                <QrCode sx={{ fontSize: 40, color: 'info.main', mr: 2 }} />
                                <Box>
                                    <Typography variant="h6">QR Code</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Generate a QR code for your Chat Space
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                Perfect for printed materials, presentations, or physical locations. Users scan and start chatting instantly.
                            </Typography>
                        </CardContent>
                        <CardActions>
                            <Button startIcon={<QrCode />} onClick={() => setQrDialog(true)}>
                                Generate QR Code
                            </Button>
                        </CardActions>
                    </Card>
                </Grid>

                {/* Direct Link */}
                <Grid item xs={12} md={4}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CardContent sx={{ flex: 1 }}>
                            <Box display="flex" alignItems="center" mb={2}>
                                <LinkIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                                <Box>
                                    <Typography variant="h6">Direct Link</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Share via link in messages or social media
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                Copy the shareable link above and send it via email, messaging apps, or post it on social media.
                            </Typography>
                        </CardContent>
                        <CardActions>
                            <Button startIcon={<ContentCopy />} onClick={() => copyToClipboard(getPublicLink())}>
                                Copy Link
                            </Button>
                        </CardActions>
                    </Card>
                </Grid>
            </Grid>

            {/* Embed Code Dialog */}
            <Dialog open={embedDialog} onClose={() => setEmbedDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>Embed Chat Space</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Choose an embed type and copy the code to paste into your website's HTML.
                    </Typography>
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Embed Type</InputLabel>
                        <Select
                            value={embedSettings.type}
                            label="Embed Type"
                            onChange={(e) => setEmbedSettings({ ...embedSettings, type: e.target.value })}
                        >
                            <MenuItem value="iframe">iFrame (Inline)</MenuItem>
                            <MenuItem value="popup">Popup Window</MenuItem>
                            <MenuItem value="slider">Slide-in Widget</MenuItem>
                        </Select>
                    </FormControl>
                    {embedSettings.type === 'iframe' && (
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="Width"
                                    value={embedSettings.width}
                                    onChange={(e) => setEmbedSettings({ ...embedSettings, width: e.target.value })}
                                    margin="normal"
                                    helperText='e.g., "100%", "800px"'
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="Height"
                                    value={embedSettings.height}
                                    onChange={(e) => setEmbedSettings({ ...embedSettings, height: e.target.value })}
                                    margin="normal"
                                    helperText='e.g., "600px", "100vh"'
                                />
                            </Grid>
                        </Grid>
                    )}
                    <TextField
                        fullWidth
                        label="Embed Code"
                        value={generateEmbedCode()}
                        multiline
                        rows={8}
                        margin="normal"
                        InputProps={{ readOnly: true }}
                        sx={{
                            '& .MuiInputBase-input': {
                                fontFamily: 'monospace',
                                fontSize: '0.85rem',
                            },
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEmbedDialog(false)}>Close</Button>
                    <Button
                        variant="contained"
                        startIcon={<ContentCopy />}
                        onClick={() => {
                            copyToClipboard(generateEmbedCode());
                            setEmbedDialog(false);
                        }}
                    >
                        Copy Code
                    </Button>
                </DialogActions>
            </Dialog>

            {/* QR Code Dialog */}
            <Dialog open={qrDialog} onClose={() => setQrDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>QR Code - Chat Space</DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" alignItems="center" p={2}>
                        <QRCodeSVG
                            id="chatspace-qr-code"
                            value={getPublicLink()}
                            size={256}
                            level="H"
                            includeMargin
                        />
                        <Typography variant="body2" color="text.secondary" mt={2}>
                            Scan to open Chat Space
                        </Typography>
                        <Typography variant="caption" color="text.secondary" mt={1} sx={{ wordBreak: 'break-all', textAlign: 'center' }}>
                            {getPublicLink()}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setQrDialog(false)}>Close</Button>
                    <Button
                        variant="contained"
                        startIcon={<Download />}
                        onClick={() => {
                            const svg = document.querySelector('#chatspace-qr-code');
                            if (svg) {
                                const svgData = new XMLSerializer().serializeToString(svg);
                                const canvas = document.createElement('canvas');
                                const ctx = canvas.getContext('2d');
                                const img = new Image();
                                img.onload = () => {
                                    canvas.width = img.width;
                                    canvas.height = img.height;
                                    ctx.drawImage(img, 0, 0);
                                    const pngFile = canvas.toDataURL('image/png');
                                    const downloadLink = document.createElement('a');
                                    downloadLink.download = `chatspace-qr-${chatbotIdentity}.png`;
                                    downloadLink.href = pngFile;
                                    downloadLink.click();
                                };
                                img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
                            }
                        }}
                    >
                        Download PNG
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ChatSpaceDistribute;
